import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { SavedViews } from "@/components/SavedViews";
import { Search, Send, X, ExternalLink, ShieldCheck, ShieldAlert, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";

const BUCKETS = [
  { k: "ready", l: "Ready to Review" },
  { k: "needs_email", l: "Needs Email Research" },
  { k: "needs_sport", l: "Needs Sport Verification" },
  { k: "quarantined", l: "Quarantined" },
  { k: "blocked", l: "Blocked" },
  { k: "scheduled", l: "Approved & Scheduled" },
  { k: "sent", l: "Sent" },
  { k: "rejected", l: "Rejected" },
];

export default function WorkQueue() {
  const [params, setParams] = useSearchParams();
  const bucket = params.get("bucket") || "ready";
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState({});
  const [bulk, setBulk] = useState(null);
  const limit = 25;
  const editableBucket = !["sent", "rejected", "scheduled"].includes(bucket);

  const load = useCallback(() => {
    api.get(`/admin/work-queue?bucket=${bucket}&q=${encodeURIComponent(q)}&skip=${skip}&limit=${limit}`)
      .then(({ data }) => setData(data)).catch(() => toast.error("Failed to load queue"));
  }, [bucket, q, skip]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); setChecked({}); }, [bucket, q]);

  const setBucket = (b) => { setParams({ bucket: b }); setSelected(null); };

  const onActioned = () => { setSelected(null); load(); };

  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl" data-testid="hq-work-queue-page">
      {typeof window !== "undefined" && window.location.hostname !== "coachconnect.ai" && window.location.hostname !== "www.coachconnect.ai" && (
        <div className="mb-5 border border-amber-500/60 bg-amber-500/10 px-4 py-2.5 flex items-start gap-2 text-xs text-amber-200/90" data-testid="preview-env-warning">
          <ShieldAlert size={14} className="text-amber-400 shrink-0 mt-0.5" />
          <span><b>Preview environment</b> — emails sent here go out for real but won't sync with production. Approve and send only from coachconnect.ai.</span>
        </div>
      )}
      <div className="text-xs tracking-widest text-volt">ADMIN HQ · OPERATIONS</div>
      <h1 className="heading text-4xl mt-1">WORK QUEUE</h1>
      <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
        Every drafted outbound email, sorted by what it needs. Nothing sends without your explicit approval.
        An item is only <b>Ready to Review</b> when it has a valid email, verified lacrosse sport, no suppression, no duplicate, and a complete draft.
      </p>

      {/* Bucket tabs */}
      <div className="mt-6 flex flex-wrap gap-1 border-b border-neutral-800" data-testid="queue-buckets">
        {BUCKETS.map((b) => (
          <button key={b.k} data-testid={`bucket-${b.k}`} onClick={() => setBucket(b.k)}
            className={`px-3 py-2.5 text-[11px] tracking-widest heading transition-colors ${
              bucket === b.k ? "border-b-2 border-blaze text-white" : "text-neutral-500 hover:text-neutral-300"
            }`}>
            {b.l.toUpperCase()} {data?.counts?.[b.k] != null ? `(${data.counts[b.k]})` : ""}
          </button>
        ))}
      </div>

      {/* Search + saved views */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Search size={14} className="text-neutral-500" />
          <input data-testid="queue-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search recipient, email, subject…"
            className="w-72 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none" />
        </div>
        <SavedViews page="work-queue" params={{ bucket, q }} onApply={(p) => { setParams({ bucket: p.bucket || "ready" }); setQ(p.q || ""); }} />
      </div>

      {/* Bulk action bar */}
      {editableBucket && data && data.items.length > 0 && (
        <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid="bulk-bar">
          <label className="inline-flex items-center gap-2 text-[11px] text-neutral-400 cursor-pointer select-none">
            <input type="checkbox" data-testid="bulk-select-all" className="accent-[#FF3B30] h-3.5 w-3.5"
              checked={data.items.length > 0 && data.items.every((i) => checked[`${i.kind}:${i.id}`])}
              onChange={(e) => {
                const next = { ...checked };
                data.items.forEach((i) => { next[`${i.kind}:${i.id}`] = e.target.checked ? i : undefined; if (!e.target.checked) delete next[`${i.kind}:${i.id}`]; });
                setChecked(next);
              }} />
            Select all on page
          </label>
          {Object.values(checked).filter(Boolean).length > 0 && (
            <>
              <span className="text-[11px] text-volt heading" data-testid="bulk-count">{Object.values(checked).filter(Boolean).length} SELECTED</span>
              {bucket === "ready" && (
                <button data-testid="bulk-approve-btn" onClick={() => setBulk("approve")}
                  className="min-h-[34px] px-4 bg-blaze text-white heading text-[10px] tracking-widest hover:glow-blaze">
                  APPROVE &amp; SEND SELECTED…
                </button>
              )}
              <button data-testid="bulk-reject-btn" onClick={() => setBulk("reject")}
                className="min-h-[34px] px-4 border border-red-900/60 text-red-400 text-[10px] tracking-widest hover:border-red-500">
                REJECT SELECTED…
              </button>
            </>
          )}
        </div>
      )}

      {/* Auto-approve verified coach_claim drafts — ready bucket */}
      {bucket === "ready" && data && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button data-testid="auto-approve-drafts-btn"
            onClick={async () => {
              try {
                const { data: dry } = await api.post("/admin/claim-drafts/auto-approve?dry_run=true");
                if (!dry.approved) {
                  toast.info(`No claim drafts pass verification right now (${dry.checked} checked${dry.held ? `, held: ${Object.entries(dry.reasons).map(([k, v]) => `${k} ${v}`).join(", ")}` : ""})`);
                  return;
                }
                if (!window.confirm(`${dry.approved} of ${dry.checked} claim drafts pass verification (valid syntax, live domain, not suppressed). Queue them for the drip? ${dry.held} will stay for manual review.`)) return;
                const { data: r } = await api.post("/admin/claim-drafts/auto-approve");
                toast.success(`${r.approved} verified drafts moved to the claim drip queue`, { description: r.held ? `${r.held} held: ${Object.entries(r.reasons).map(([k, v]) => `${k} ${v}`).join(", ")}` : "Nothing held back.", duration: 9000 });
                load();
              } catch (e) { toast.error(e.response?.data?.detail || "Auto-approve failed"); }
            }}
            className="min-h-[38px] px-4 border border-emerald-500/70 text-emerald-300 heading text-[10px] tracking-widest hover:bg-emerald-500 hover:text-black transition-colors">
            AUTO-APPROVE VERIFIED DRAFTS
          </button>
          <span className="text-[10px] text-neutral-500">Checks each claim draft's recipient (syntax · MX · not bounced/unsubscribed · coach still unclaimed) and queues the passes for the daily drip. Also runs nightly 12:45 UTC. Nothing sends here.</span>
        </div>
      )}

      {/* AI email research — only useful on the needs_email bucket */}
      {bucket === "needs_email" && data && data.counts.needs_email > 0 && (
        <div className="mt-3">
          <button data-testid="research-emails-btn"
            onClick={async () => {
              try {
                const { data: r } = await api.post("/admin/email-research/run");
                toast.success("Claude is researching missing emails", { description: r.note });
              } catch (e) { toast.error(e.response?.data?.detail || "Failed to start research"); }
            }}
            className="min-h-[38px] px-4 border border-volt text-volt heading text-[10px] tracking-widest hover:bg-volt hover:text-white transition-colors">
            RESEARCH MISSING EMAILS WITH AI
          </button>
          <button data-testid="research-coach-emails-btn"
            onClick={async () => {
              try {
                const { data: r } = await api.post("/admin/email-research/coaches/run?limit=100");
                toast.success(`Coach email hunter started — ${r.discoverable_profiles ?? 0} coaches discoverable, ${r.eligible_profiles ?? 0} bio pages ready`, { description: r.note, duration: 10000 });
              } catch (e) { toast.error(e.response?.data?.detail || "Failed to start coach research"); }
            }}
            className="ml-2 min-h-[38px] px-4 border border-volt/60 text-volt heading text-[10px] tracking-widest hover:bg-volt hover:text-white transition-colors">
            HUNT COACH PROFILE EMAILS (BATCH OF 100)
          </button>
          <span className="ml-3 text-[10px] text-neutral-500">Two steps: DuckDuckGo finds each coach's bio/staff page, Claude extracts + verifies the email. Found emails auto-refill the claim queue. Also runs nightly. Nothing is sent.</span>
        </div>
      )}

      {/* List */}
      {!data ? (
        <div className="mt-8 text-neutral-500 text-sm">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="mt-6 border border-dashed border-neutral-800 p-10 text-neutral-500" data-testid="queue-empty">
          Nothing in this queue. {bucket === "ready" ? "All caught up." : ""}
        </div>
      ) : (
        <>
          <div className="mt-4 border border-neutral-800 divide-y divide-neutral-900" data-testid="queue-list">
            {data.items.map((it) => (
              <div key={`${it.kind}-${it.id}`} className={`flex items-center ${selected?.id === it.id ? "bg-neutral-900/60" : "bg-appcard"}`}>
                {editableBucket && (
                  <input type="checkbox" data-testid={`queue-check-${it.id}`}
                    className="accent-[#FF3B30] h-3.5 w-3.5 ml-3 shrink-0"
                    checked={!!checked[`${it.kind}:${it.id}`]}
                    onChange={(e) => {
                      const next = { ...checked };
                      if (e.target.checked) next[`${it.kind}:${it.id}`] = it; else delete next[`${it.kind}:${it.id}`];
                      setChecked(next);
                    }} />
                )}
                <button data-testid={`queue-row-${it.id}`} onClick={() => setSelected(it)}
                  className="flex-1 min-w-0 text-left px-4 py-3 hover:bg-neutral-900/50 transition-colors flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white font-medium">{it.recipient_name || "Unknown recipient"}</span>
                    <KindBadge item={it} />
                    {it.unsubscribed && <MiniBadge red>UNSUBSCRIBED</MiniBadge>}
                    {it.bounced && <MiniBadge red>BOUNCED</MiniBadge>}
                    {it.duplicate && <MiniBadge amber>DUPLICATE</MiniBadge>}
                  </div>
                  <div className="text-xs text-neutral-400 truncate mt-0.5">{it.subject || "(no subject)"}</div>
                  <div className="text-[10px] text-neutral-600 mt-0.5">{it.context} {it.recipient_email ? `· ${it.recipient_email}` : "· no email on file"}</div>
                </div>
                <ChevronRight size={14} className="text-neutral-600 shrink-0" />
                </button>
              </div>
            ))}
          </div>
          <Pager skip={skip} limit={limit} total={data.total} onChange={setSkip} />
        </>
      )}

      {selected && <DetailDrawer item={selected} bucket={bucket} onClose={() => setSelected(null)} onActioned={onActioned} />}
      {bulk && (
        <BulkModal
          mode={bulk}
          items={Object.values(checked).filter(Boolean)}
          onClose={() => setBulk(null)}
          onDone={() => { setBulk(null); setChecked({}); load(); }}
        />
      )}
    </div>
  );
}

function BulkModal({ mode, items, onClose, onDone }) {
  const [progress, setProgress] = useState(null);
  const approve = mode === "approve";
  const run = async () => {
    let ok = 0, fail = 0;
    const failures = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      setProgress({ done: i, total: items.length, current: it.recipient_name });
      try {
        if (approve) {
          await api.post(`/admin/email-queue/${it.kind}/${it.id}/send`, {
            to: it.recipient_email, subject: it.subject, body: it.body,
          });
        } else {
          await api.post(`/admin/email-queue/${it.kind}/${it.id}/reject`);
        }
        ok++;
      } catch (e) {
        fail++;
        failures.push(`${it.recipient_name || it.recipient_email}: ${e.response?.data?.detail || "failed"}`);
      }
    }
    if (fail === 0) toast.success(`${approve ? "Sent" : "Rejected"} ${ok} of ${items.length}`);
    else toast.error(`${approve ? "Sent" : "Rejected"} ${ok}, ${fail} failed`, { description: failures.slice(0, 3).join(" · "), duration: 12000 });
    onDone();
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={progress ? undefined : onClose} data-testid="bulk-confirm-modal">
      <div className={`bg-[#0B0B0E] border max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto ${approve ? "border-blaze/70" : "border-red-900/70"}`} onClick={(e) => e.stopPropagation()}>
        <div className="heading text-2xl">{approve ? "APPROVE & SEND" : "REJECT"} {items.length} DRAFT{items.length === 1 ? "" : "S"}?</div>
        <p className="text-xs text-neutral-400 mt-2">
          {approve
            ? "These emails go out immediately, one by one. Suppressed or bounced recipients are blocked automatically by the server."
            : "These drafts are removed from the queue. Nothing is sent."}
        </p>
        <div className="mt-4 space-y-1 max-h-56 overflow-y-auto" data-testid="bulk-recipient-list">
          {items.map((it) => (
            <div key={`${it.kind}:${it.id}`} className="border border-neutral-800 px-2.5 py-1.5 text-xs flex items-center gap-2">
              <span className="text-white">{it.recipient_name || "Unknown"}</span>
              <span className="text-neutral-500 font-mono text-[10px] ml-auto">{it.recipient_email || "no email"}</span>
            </div>
          ))}
        </div>
        {progress && (
          <div className="mt-4 text-xs text-volt" data-testid="bulk-progress">
            {approve ? "Sending" : "Rejecting"} {progress.done + 1} of {progress.total} — {progress.current}…
          </div>
        )}
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} disabled={!!progress} className="px-4 py-2.5 text-xs tracking-widest text-neutral-400 hover:text-white disabled:opacity-40">CANCEL</button>
          <button data-testid="bulk-confirm-btn" onClick={run} disabled={!!progress}
            className={`px-5 py-2.5 heading text-xs tracking-widest text-white disabled:opacity-50 ${approve ? "bg-blaze hover:glow-blaze" : "bg-red-700 hover:bg-red-600"}`}>
            {progress ? "WORKING…" : approve ? `CONFIRM — SEND ${items.length}` : `CONFIRM — REJECT ${items.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Pager({ skip, limit, total, onChange }) {
  if (total == null || total <= limit) return <div className="mt-3 text-[11px] text-neutral-600">{total ?? 0} records</div>;
  return (
    <div className="mt-3 flex items-center gap-3 text-[11px] text-neutral-500" data-testid="queue-pager">
      <button data-testid="pager-prev" disabled={skip === 0} onClick={() => onChange(Math.max(0, skip - limit))}
        className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40 hover:border-volt"><ChevronLeft size={12} /></button>
      <span>{skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
      <button data-testid="pager-next" disabled={skip + limit >= total} onClick={() => onChange(skip + limit)}
        className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40 hover:border-volt"><ChevronRight size={12} /></button>
    </div>
  );
}

function MiniBadge({ children, red, amber }) {
  return (
    <span className={`text-[9px] tracking-widest px-1.5 py-0.5 border ${red ? "border-red-600 text-red-500" : amber ? "border-amber-500/60 text-amber-400" : "border-neutral-600 text-neutral-400"}`}>
      {children}
    </span>
  );
}

function KindBadge({ item }) {
  const k = item.pitch_kind || item.kind;
  const map = {
    coach_claim: ["COACH CLAIM INVITE", "border-sky-500/60 text-sky-400"],
    outreach: ["VACANCY PITCH", "border-blaze/60 text-blaze"],
    hc_admin_style: ["HC PITCH", "border-volt/60 text-volt"],
    job_invite: ["JOB INVITE", "border-volt/60 text-volt"],
    testimonial_request: ["TESTIMONIAL ASK", "border-neutral-600 text-neutral-400"],
  };
  const [label, cls] = map[k] || ["HC PITCH", "border-volt/60 text-volt"];
  return <span className={`text-[9px] tracking-widest px-1.5 py-0.5 border ${cls}`}>{label}</span>;
}

function DetailDrawer({ item, bucket, onClose, onActioned }) {
  const [to, setTo] = useState(item.recipient_email || "");
  const [subject, setSubject] = useState(item.subject || "");
  const [body, setBody] = useState(item.body || "");
  const [busy, setBusy] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const editable = !["sent", "rejected", "scheduled"].includes(bucket);

  useEffect(() => {
    setTo(item.recipient_email || ""); setSubject(item.subject || ""); setBody(item.body || "");
    setConfirming(false);
  }, [item]);
  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 6000);
    return () => clearTimeout(t);
  }, [confirming]);

  const save = async () => {
    setBusy("save");
    try {
      await api.patch(`/admin/email-queue/${item.kind}/${item.id}`, { subject, body, to: to || undefined });
      toast.success("Draft saved");
    } catch { toast.error("Save failed"); }
    setBusy(null);
  };
  const send = async () => {
    if (!to.includes("@")) return toast.error("Enter a recipient email first");
    if (!confirming) return setConfirming(true);
    setConfirming(false); setBusy("send");
    try {
      await api.post(`/admin/email-queue/${item.kind}/${item.id}/send`, { to, subject, body });
      toast.success(`✓ Sent to ${to}`, { description: "Auto follow-up fires in 7 days if no signup." });
      onActioned();
    } catch (e) {
      toast.error(`Send failed — NOT sent to ${to}`, { description: e.response?.data?.detail || "Try again or check Delivery.", duration: 10000 });
      setBusy(null);
    }
  };
  const reject = async () => {
    setBusy("reject");
    try {
      await api.post(`/admin/email-queue/${item.kind}/${item.id}/reject`);
      toast.success("Draft rejected");
      onActioned();
    } catch { toast.error("Reject failed"); setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="queue-detail-drawer">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full max-w-xl bg-[#0B0B0E] border-l border-neutral-800 h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#0B0B0E] border-b border-neutral-800 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div>
            <div className="heading text-xl">{item.recipient_name || "Unknown recipient"}</div>
            <div className="text-xs text-neutral-500 mt-0.5">{item.context}</div>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="text-neutral-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Verification badges */}
          <div className="flex flex-wrap gap-1.5">
            <KindBadge item={item} />
            {item.sport_verified
              ? <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-emerald-500/60 text-emerald-400 inline-flex items-center gap-1"><ShieldCheck size={10} /> SPORT VERIFIED</span>
              : item.sport_mismatch
                ? <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-red-600 text-red-500 inline-flex items-center gap-1"><ShieldAlert size={10} /> {String(item.sport_mismatch).toUpperCase()} — NOT LACROSSE</span>
                : <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-amber-500/60 text-amber-400 inline-flex items-center gap-1"><ShieldAlert size={10} /> SPORT UNVERIFIED</span>}
            {item.unsubscribed && <MiniBadge red>UNSUBSCRIBED</MiniBadge>}
            {item.bounced && <MiniBadge red>BOUNCED</MiniBadge>}
            {item.duplicate && <MiniBadge amber>DUPLICATE PENDING DRAFT</MiniBadge>}
          </div>

          {/* Why blocked */}
          {(item.blocked_reasons || []).length > 0 && (
            <div className="border border-amber-700/50 bg-amber-950/20 p-3" data-testid="drawer-blocked-reasons">
              <div className="text-[10px] tracking-widest text-amber-400 flex items-center gap-1.5"><AlertTriangle size={11} /> WHY THIS ITEM IS BLOCKED</div>
              <ul className="mt-1.5 space-y-1">
                {item.blocked_reasons.map((r, i) => <li key={i} className="text-xs text-amber-200/80">• {r}</li>)}
              </ul>
            </div>
          )}

          {/* Source evidence */}
          {item.sport_evidence && (
            <div className="border-l-2 border-emerald-500/60 bg-emerald-950/20 px-3 py-2" data-testid="drawer-evidence">
              <div className="text-[9px] tracking-widest text-emerald-400">SOURCE QUOTATION</div>
              <div className="text-xs text-neutral-300 mt-1 italic">&ldquo;{item.sport_evidence}&rdquo;</div>
            </div>
          )}
          {item.source_url && (
            <a href={item.source_url} target="_blank" rel="noreferrer" data-testid="drawer-source-link"
              className="text-xs text-neutral-400 hover:text-volt inline-flex items-center gap-1">
              Original source posting <ExternalLink size={11} />
            </a>
          )}

          {/* Sends history */}
          {item.sent_at && (
            <div className="text-[11px] text-neutral-500 border border-neutral-800 p-2.5" data-testid="drawer-send-history">
              Sent {new Date(item.sent_at).toLocaleString()} → {item.sent_to || item.recipient_email}
              {item.followup_sent_at ? ` · follow-up sent ${new Date(item.followup_sent_at).toLocaleDateString()}` : item.followup_skipped ? " · follow-up skipped" : " · follow-up fires 7 days after send if no signup"}
            </div>
          )}

          {/* Draft editor */}
          <label className="block">
            <span className="text-[10px] tracking-widest text-neutral-500">RECIPIENT</span>
            <input data-testid="drawer-to" value={to} onChange={(e) => setTo(e.target.value)} disabled={!editable}
              className="mt-1 w-full bg-appbg border border-neutral-700 px-3 py-2 text-sm focus:border-blaze outline-none disabled:opacity-60" />
          </label>
          <label className="block">
            <span className="text-[10px] tracking-widest text-neutral-500">SUBJECT</span>
            <input data-testid="drawer-subject" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!editable}
              className="mt-1 w-full bg-appbg border border-neutral-700 px-3 py-2 text-sm focus:border-blaze outline-none disabled:opacity-60" />
          </label>
          <label className="block">
            <span className="text-[10px] tracking-widest text-neutral-500">EMAIL BODY</span>
            <textarea data-testid="drawer-body" value={body} onChange={(e) => setBody(e.target.value)} rows={12} disabled={!editable}
              className="mt-1 w-full bg-appbg border border-neutral-700 px-3 py-2 text-sm leading-relaxed focus:border-blaze outline-none font-mono disabled:opacity-60" />
          </label>

          {editable && (
            <div className="flex items-center gap-2 flex-wrap pt-1 pb-6">
              <button data-testid="drawer-approve-send" onClick={send} disabled={!!busy || item.unsubscribed || item.bounced}
                className={`min-h-[40px] px-4 heading text-xs tracking-widest transition-all disabled:opacity-50 flex items-center gap-1.5 ${
                  confirming ? "bg-red-600 text-white animate-pulse" : "bg-blaze text-white hover:glow-blaze"}`}>
                <Send size={13} /> {busy === "send" ? "SENDING…" : confirming ? `CONFIRM — SEND TO ${to.toUpperCase()}` : "APPROVE & SEND"}
              </button>
              <button data-testid="drawer-save" onClick={save} disabled={!!busy}
                className="min-h-[40px] px-4 border border-neutral-700 text-neutral-300 text-xs tracking-widest hover:border-volt hover:text-volt disabled:opacity-50">
                {busy === "save" ? "SAVING…" : "SAVE EDITS"}
              </button>
              <button data-testid="drawer-reject" onClick={reject} disabled={!!busy}
                className="min-h-[40px] px-4 border border-red-900/60 text-red-400 text-xs tracking-widest hover:border-red-500 disabled:opacity-50 flex items-center gap-1.5">
                <X size={13} /> REJECT
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
